/**
 * FunnelStagesList — empty state ou board horizontal de etapas.
 */
import React from 'react';
import { AppIcon } from '../../../../components/icons/AppIcon';
import { Button } from '../../../../components/ui/Button';
import { cn } from '@/lib/utils';
import FunnelStageRow, { FunnelStageData } from './FunnelStageRow';

interface FunnelStagesListProps {
    stages: FunnelStageData[];
    draggedIndex: number | null;
    showConversionEvent?: boolean;
    onNameChange: (index: number, value: string) => void;
    onDeadlineChange: (index: number, value: number) => void;
    onConversionEventChange?: (index: number, value: string) => void;
    onRemove: (index: number) => void | Promise<void>;
    onDragStart: (index: number) => void;
    onDragOver: (e: React.DragEvent, index: number) => void;
    onDragEnd: () => void;
    onAddStage: () => void;
    onAddStageAt?: (index: number) => void;
}

const COL_WIDTH = 'w-[18rem]';

const INSERT_BTN = cn(
    'absolute top-s4 right-0 z-10 size-5 translate-x-1/2 rounded-full',
    'bg-primary text-primary-foreground shadow-sm ring-2 ring-card',
    'pointer-events-none opacity-0 transition-opacity duration-100',
    'group-hover/stage:pointer-events-auto group-hover/stage:opacity-100',
    'focus-visible:pointer-events-auto focus-visible:opacity-100',
);

function AddStagePanel({
    onAddStage,
    aside = false,
}: {
    onAddStage: () => void;
    aside?: boolean;
}) {
    const headingId = aside ? 'funnel-add-stage-aside' : 'funnel-add-stage-heading';

    return (
        <section
            className={cn(
                'flex w-full flex-col items-center justify-center bg-background text-center',
                aside ? 'px-s3' : 'min-h-0 flex-1 px-s6 py-s12',
            )}
            aria-labelledby={headingId}
        >
            <div className="mx-auto flex w-full max-w-[16rem] flex-col items-center gap-s2">
                <h2
                    id={headingId}
                    className={cn(
                        'w-full text-balance font-semibold tracking-tight text-default',
                        aside ? 'text-lg' : 'text-xl sm:text-2xl',
                    )}
                >
                    Adicionar nova etapa
                </h2>
                <p className="card-sub w-full text-pretty">
                    As etapas do funil representam os passos em seu processo de vendas
                </p>
                <Button
                    type="button"
                    variant="secondary"
                    onClick={onAddStage}
                    className="mt-s1 min-h-9 touch-manipulation"
                >
                    <AppIcon name="plus" className="size-4" aria-hidden />
                    Nova etapa
                </Button>
            </div>
        </section>
    );
}

const FunnelStagesList: React.FC<FunnelStagesListProps> = ({
    stages,
    draggedIndex,
    showConversionEvent = true,
    onNameChange,
    onDeadlineChange,
    onConversionEventChange,
    onRemove,
    onDragStart,
    onDragOver,
    onDragEnd,
    onAddStage,
    onAddStageAt,
}) => {
    const insertAt = (index: number) => {
        if (onAddStageAt) onAddStageAt(index);
        else onAddStage();
    };

    if (stages.length === 0) {
        return <AddStagePanel onAddStage={onAddStage} />;
    }

    return (
        <section className="flex min-h-0 min-w-0 flex-1 flex-col" aria-labelledby="funnel-stages-heading">
            <h2 id="funnel-stages-heading" className="sr-only">
                Etapas do funil
            </h2>

            <div className="custom-scrollbar min-w-0 overflow-x-auto overscroll-x-contain pb-s2">
                <ol className="m-0 flex w-max list-none items-stretch gap-s2 p-0 pr-s4">
                    {stages.map((stage, index) => {
                        const label = stage.name.trim() || `etapa ${index + 1}`;

                        return (
                            <li
                                key={stage.id || `stage-${index}`}
                                className="group/stage relative shrink-0"
                            >
                                <FunnelStageRow
                                    stage={stage}
                                    index={index}
                                    isDragging={draggedIndex === index}
                                    showConversionEvent={showConversionEvent}
                                    onNameChange={(value) => onNameChange(index, value)}
                                    onDeadlineChange={(value) => onDeadlineChange(index, value)}
                                    onConversionEventChange={
                                        onConversionEventChange
                                            ? (value) => onConversionEventChange(index, value)
                                            : undefined
                                    }
                                    onRemove={() => onRemove(index)}
                                    onDragStart={() => onDragStart(index)}
                                    onDragOver={(e) => onDragOver(e, index)}
                                    onDragEnd={onDragEnd}
                                />

                                <Button
                                    type="button"
                                    size="icon-xs"
                                    aria-label={`Inserir etapa após ${label}`}
                                    onClick={() => insertAt(index + 1)}
                                    className={INSERT_BTN}
                                >
                                    <AppIcon name="plus" className="size-3" aria-hidden />
                                </Button>
                            </li>
                        );
                    })}

                    <li
                        className={cn(
                            COL_WIDTH,
                            'flex shrink-0 flex-col items-center justify-center self-stretch',
                        )}
                    >
                        <AddStagePanel onAddStage={onAddStage} aside />
                    </li>
                </ol>
            </div>
        </section>
    );
};

export default FunnelStagesList;
